import { ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { cn } from '@/shared/lib/utils';
import { Text } from '@/shared/ui/text';
import { CloseButton } from '../button/CloseButton';
import { dropzoneVariants } from './helpers/variants';

/**
 * Dropzone コンポーネントの Props
 */
/** 単一ファイルモード */
type SingleFileMode = {
  multiple?: false;
  maxFiles?: never;
};

/** 複数ファイルモード */
type MultipleFileMode = {
  /** 複数ファイル許可 */
  multiple: true;
  /** 最大ファイル数 */
  maxFiles?: number;
};

type BaseDropzoneProps = {
  /** 受け入れるファイルタイプ（例: { 'application/pdf': ['.pdf'] }） */
  accept?: Accept;
  /** 最大ファイルサイズ（bytes） */
  maxSize?: number;
  /** ファイルドロップ時のコールバック */
  onDrop?: (files: File[]) => void;
  /** ファイル拒否時のコールバック */
  onDropRejected?: (rejections: FileRejection[]) => void;
  /** ファイル削除時のコールバック */
  onRemove?: (file: File) => void;
  /** メインテキスト（例: "PDFをドラッグ＆ドロップ"） */
  title?: string;
  /** サブテキスト（例: "またはクリックして選択"） */
  subtitle?: string;
  /** 説明テキスト（例: "PDF形式・最大20MB"） */
  description?: string;
  /** カスタムアイコン */
  icon?: ReactNode;
  /** 無効状態 */
  disabled?: boolean;
  /**
   * ファイル数上限に達したらドロップエリアを非表示にする（デフォルト: false）
   * - 単一モード: ファイルが1つ選択されると非表示
   * - 複数モード: maxFiles に達すると非表示
   */
  hideOnMaxFiles?: boolean;
  /** 追加のクラス名 */
  className?: string;
};

export type DropzoneProps = BaseDropzoneProps & (SingleFileMode | MultipleFileMode);

/**
 * ファイルサイズを人間が読みやすい形式にフォーマット
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Dropzone コンポーネント
 *
 * ドラッグ＆ドロップでファイルをアップロードするためのコンポーネント。
 * react-dropzone を使用して、ファイル選択のUIを提供する。
 *
 * @example
 * // 単一PDFアップロード
 * <Dropzone
 *   accept={{ 'application/pdf': ['.pdf'] }}
 *   maxSize={20 * 1024 * 1024}
 *   title="PDFをドラッグ＆ドロップ"
 *   subtitle="またはクリックして選択"
 *   description="PDF形式・最大20MB"
 *   onDrop={(files) => handleUpload(files[0])}
 * />
 *
 * @example
 * // 複数画像アップロード
 * <Dropzone
 *   accept={{ 'image/*': ['.png', '.jpg', '.jpeg'] }}
 *   maxSize={5 * 1024 * 1024}
 *   multiple
 *   maxFiles={5}
 *   title="画像をドラッグ＆ドロップ"
 *   onDrop={(files) => handleImages(files)}
 * />
 */
export const Dropzone: FC<DropzoneProps> = ({
  accept,
  maxSize,
  multiple = false,
  maxFiles,
  onDrop,
  onDropRejected,
  onRemove,
  title = 'ファイルをドラッグ＆ドロップ',
  subtitle = 'またはクリックして選択',
  description,
  icon,
  disabled = false,
  hideOnMaxFiles = false,
  className,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDrop = (acceptedFiles: File[]) => {
    setErrorMessage(null);
    setSelectedFiles(acceptedFiles);
    onDrop?.(acceptedFiles);
  };

  const handleDropRejected = (rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (rejection) {
      const error = rejection.errors[0];
      if (error) {
        switch (error.code) {
          case 'file-too-large':
            setErrorMessage(
              `ファイルサイズが大きすぎます（最大: ${maxSize ? formatFileSize(maxSize) : '制限なし'}）`,
            );
            break;
          case 'file-invalid-type':
            setErrorMessage('このファイル形式は対応していません');
            break;
          case 'too-many-files':
            setErrorMessage(`ファイル数が多すぎます（最大: ${maxFiles ?? '制限なし'}ファイル）`);
            break;
          default:
            setErrorMessage('ファイルを選択できませんでした');
        }
      }
    }
    onDropRejected?.(rejections);
  };

  const handleRemoveFile = (file: File) => {
    setSelectedFiles((prev) => prev.filter((f) => f !== file));
    onRemove?.(file);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    ...(accept && { accept }),
    ...(maxSize && { maxSize }),
    multiple,
    ...(maxFiles && { maxFiles }),
    disabled,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
  });

  // 状態に応じたバリアントを決定
  const getState = () => {
    if (disabled) return 'disabled';
    if (isDragReject) return 'rejected';
    if (isDragActive) return 'active';
    return 'default';
  };

  // ファイル数上限に達したかどうか
  // - シングルモード（multiple=false）: 1ファイル選択で上限
  // - マルチモード（multiple=true）: maxFiles 指定時のみ上限判定
  const isMaxFilesReached = multiple
    ? maxFiles !== undefined && selectedFiles.length >= maxFiles
    : selectedFiles.length >= 1;

  // ドロップエリアを非表示にするかどうか
  const shouldHideDropzone = hideOnMaxFiles && isMaxFilesReached;

  return (
    <div className="w-full">
      {!shouldHideDropzone && (
        <div {...getRootProps()} className={cn(dropzoneVariants({ state: getState() }), className)}>
          <input {...getInputProps()} />

          <div className="flex flex-col items-center gap-2">
            {/* アイコン */}
            <div className="text-text-secondary">
              {icon ?? <ArrowUpTrayIcon className="h-6 w-6" />}
            </div>

            {/* メインテキスト */}
            <Text variant="body">{title}</Text>

            {/* サブテキスト */}
            <Text variant="description" color="success">
              {subtitle}
            </Text>

            {/* 説明テキスト */}
            {description && <Text variant="description">{description}</Text>}
          </div>
        </div>
      )}

      {/* エラーメッセージ */}
      {errorMessage && (
        <Text variant="body" color="error" className="mt-2">
          {errorMessage}
        </Text>
      )}

      {/* 選択されたファイル一覧 */}
      {selectedFiles.length > 0 && (
        <div className={cn('space-y-2', !shouldHideDropzone && 'mt-4')}>
          {selectedFiles.map((file) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center justify-between rounded-md border border-stone-200 bg-stone-50 px-4 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <DocumentTextIcon className="h-4 w-4 text-text-secondary" />
                <Text variant="body" truncate>
                  {file.name}
                </Text>
                <Text variant="description">({formatFileSize(file.size)})</Text>
              </div>
              <CloseButton
                onClick={() => handleRemoveFile(file)}
                aria-label={`${file.name}を削除`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

Dropzone.displayName = 'Dropzone';
